<template>
  <div class="dashboard-page">
    <div class="dashboard-container">
      <header class="dashboard-header">
        <div class="header-left">
          <div class="greeting-section">
            <h1>{{ greeting }}, {{ userName }}!</h1>
            <p class="subtitle" v-if="!isAdminOrAgent">Welcome back to your IT command center.</p>
            <div class="header-breadcrumb" v-if="isAdminOrAgent">
              <span class="muted">IT Support</span> / <span class="active">Support Overview</span>
            </div>
          </div>
        </div>
        <button @click="showCreateModal = true" class="primary-btn">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          {{ isAdminOrAgent ? 'Add New' : 'New Ticket' }}
        </button>
      </header>

      <!-- ============================================== -->
      <!-- ADMIN / AGENT VIEW (HostoGo Style)             -->
      <!-- ============================================== -->
      <div v-if="isAdminOrAgent" class="admin-view">
        <!-- Top Stats Row -->
        <div class="stats-row">
          <div class="stat-card glass-card">
            <div class="stat-header">
               <span class="stat-title">All tickets</span>
               <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#cbd5e1" stroke-width="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            </div>
            <span class="stat-value">{{ stats.total }}</span>
          </div>
          
          <div class="stat-card glass-card">
            <div class="stat-header">
               <span class="stat-title">New Tickets</span>
               <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#64748b" stroke-width="1.5"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
            </div>
            <span class="stat-value">{{ stats.new }}</span>
          </div>

          <div class="stat-card glass-card">
            <div class="stat-header">
               <span class="stat-title">In Progress</span>
               <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#d97706" stroke-width="1.5"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            </div>
            <span class="stat-value">{{ stats.inProgress }}</span>
          </div>

          <div class="stat-card glass-card">
            <div class="stat-header">
               <span class="stat-title">Pending User</span>
               <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#b45309" stroke-width="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
            </div>
            <span class="stat-value">{{ stats.pendingUser }}</span>
          </div>

          <div class="stat-card glass-card">
            <div class="stat-header">
               <span class="stat-title">Resolved</span>
               <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#16a34a" stroke-width="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </div>
            <span class="stat-value">{{ stats.resolved }}</span>
          </div>
        </div>

        <!-- Middle Grid: Area Chart & Gauge -->
        <div class="middle-grid">
          <div class="glass-card chart-card area-chart-card">
            <div class="chart-header">
              <h3>Total Ticket Activity</h3>
              <span class="chart-val">{{ tickets.length }}</span>
            </div>
            <div class="css-area-chart">
               <div class="line-path"></div>
               <div class="gradient-fill"></div>
               <div class="data-point tooltip-anchor">
                  <div class="tooltip">
                     <span class="t-date">All Time</span>
                     <span class="t-val">{{ tickets.length }} Tickets</span>
                  </div>
               </div>
            </div>
          </div>

          <div class="glass-card chart-card gauge-card">
            <h3>Ticket priority</h3>
            <div class="gauge-container">
              <div class="gauge-wrapper">
                <div class="gauge-arc email" :style="{ opacity: priorityStats.critical ? 1 : 0.2 }"></div>
                <div class="gauge-arc messenger" :style="{ opacity: priorityStats.high ? 1 : 0.2 }"></div>
                <div class="gauge-arc live-chat" :style="{ opacity: priorityStats.medium ? 1 : 0.2 }"></div>
                <div class="gauge-arc contact-form" :style="{ opacity: priorityStats.low ? 1 : 0.2 }"></div>
                <div class="gauge-center">
                   <span class="g-lbl">Total active tickets</span>
                   <span class="g-val">{{ stats.new + stats.inProgress + stats.pendingUser }}</span>
                </div>
              </div>
            </div>
            <div class="legend-grid">
               <div class="leg-item"><span class="dot d-email"></span> Critical ({{ priorityStats.critical }})</div>
               <div class="leg-item"><span class="dot d-chat"></span> High ({{ priorityStats.high }})</div>
               <div class="leg-item"><span class="dot d-mess"></span> Medium ({{ priorityStats.medium }})</div>
               <div class="leg-item"><span class="dot d-form"></span> Low ({{ priorityStats.low }})</div>
            </div>
          </div>
        </div>

        <!-- Bottom Grid: Bar Chart & Recent List -->
        <div class="bottom-grid">
          <div class="glass-card chart-card bar-chart-card">
            <div class="chart-header-row">
              <h3>Recent Tickets Created</h3>
              <button class="view-btn" @click="router.push('/board')">Full View ↗</button>
            </div>
            <div class="bar-legends">
              <div class="leg-item"><span class="square s-sol"></span> Resolved <strong class="ml">{{ stats.resolved }}</strong></div>
              <div class="leg-item"><span class="square s-cre"></span> Total <strong class="ml">{{ tickets.length }}</strong></div>
            </div>
            <div class="css-bar-chart">
              <div class="y-axis">
                <span>{{ Math.max(10, tickets.length) }}</span><span></span><span></span><span></span><span></span><span>0</span>
              </div>
              <div class="bars">
                 <div class="bar-col" v-for="day in last7Days" :key="day.dateStr">
                   <div class="b-top" :style="{ height: (day.openHeight) + '%' }"></div>
                   <div class="b-bot" :style="{ height: (day.resolvedHeight) + '%' }"></div>
                   <span class="x-lbl">{{ day.label }}</span>
                 </div>
              </div>
            </div>
          </div>

          <div class="glass-card recent-card">
            <div class="chart-header-row">
              <h3>Recent tickets</h3>
              <router-link to="/board" class="view-btn" style="text-decoration:none;">View all ↗</router-link>
            </div>
            
            <div class="ticket-list">
              <div v-if="loading" class="table-loading"><div class="spinner"></div></div>
              <div v-else-if="filteredTickets.length">
                 <div v-for="ticket in filteredTickets.slice(0, 6)" :key="ticket.id" class="ticket-row" @click="router.push(`/ticket/${ticket.id}`)">
                   <div class="t-line" :class="ticket.status.toLowerCase()"></div>
                   <div class="t-date">
                      <span class="month">{{ new Date(ticket.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }}</span>
                   </div>
                   <div class="t-info">
                      <span class="t-title">{{ ticket.title }}</span>
                      <span class="t-desc">{{ ticket.description?.substring(0, 40) }}...</span>
                   </div>
                   <div class="t-status">
                      <span :class="['status-badge', ticket.status.toLowerCase(), { 'overdue': isOverdue(ticket.due_date) && ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' }]">
                        {{ ticket.status.replace('_', ' ') }}
                      </span>
                      <div class="arrow-ico" v-if="ticket.status === 'NEW'">↗</div>
                   </div>
                 </div>
              </div>
              <div v-else class="empty-state"><p>No tickets found.</p></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================== -->
      <!-- STANDARD USER VIEW                             -->
      <!-- ============================================== -->
      <div v-else class="user-view">
        <div class="user-stats-row">
          <div class="stat-card accent-blue">
            <div class="stat-icon">📂</div>
            <div class="stat-details">
              <span class="val">{{ stats.new }}</span>
              <span class="lbl">New Tickets</span>
            </div>
          </div>
          <div class="stat-card accent-amber">
            <div class="stat-icon">⏳</div>
            <div class="stat-details">
              <span class="val">{{ stats.inProgress }}</span>
              <span class="lbl">In Progress</span>
            </div>
          </div>
          <div class="stat-card accent-orange">
            <div class="stat-icon">👤</div>
            <div class="stat-details">
              <span class="val">{{ stats.pendingUser }}</span>
              <span class="lbl">Pending User</span>
            </div>
          </div>
          <div class="stat-card accent-green">
            <div class="stat-icon">✅</div>
            <div class="stat-details">
              <span class="val">{{ stats.resolved }}</span>
              <span class="lbl">Resolved</span>
            </div>
          </div>
        </div>

        <section class="tickets-section">
          <div class="section-header">
            <h3>My Recent Tickets</h3>
            <div class="filters-bar">
              <input v-model="searchQuery" placeholder="Search ticket..." class="search-input" />
              <select v-model="filterStatus" class="filter-select">
                <option value="">All Status</option>
                <option value="NEW">New</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PENDING_USER">Pending User</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
              </select>
              <select v-model="filterCategory" class="filter-select">
                <option value="">All Category</option>
                <option value="Hardware">Hardware</option>
                <option value="Software">Software</option>
                <option value="Network">Network</option>
                <option value="Email">Email</option>
                <option value="Printer">Printer</option>
                <option value="SAP">SAP</option>
                <option value="Access Request">Access Request</option>
                <option value="Others">Others</option>
              </select>
            </div>
            <span class="count-tag">{{ filteredTickets.length }} Total</span>
          </div>
          
          <div class="ticket-table-card">
            <div v-if="loading" class="table-loading">
               <div class="spinner"></div>
               <p>Refreshing tickets...</p>
            </div>
            <div v-else-if="filteredTickets.length" class="table-wrapper">
              <table class="user-table">
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>SLA Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in filteredTickets" :key="ticket.id" @click="router.push(`/ticket/${ticket.id}`)">
                    <td><span class="id-badge">#{{ ticket.id }}</span></td>
                    <td class="title-cell">
                      <div class="title-wrap">
                        <span class="title-text">{{ ticket.title }}</span>
                        <span :class="['p-tag', ticket.priority.toLowerCase()]">{{ ticket.priority }}</span>
                      </div>
                    </td>
                    <td><span :class="['s-pill', ticket.status.toLowerCase()]">{{ ticket.status.replace('_', ' ') }}</span></td>
                    <td class="sla-cell" :class="{ urgent: isOverdue(ticket.due_date) }">
                      {{ new Date(ticket.due_date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="user-empty-state">
              <div class="empty-art">🎫</div>
              <h4>No tickets found</h4>
              <p>Create your first ticket to get started.</p>
            </div>
          </div>
        </section>
      </div>

    </div>

    <!-- Create Ticket Modal -->
    <div v-if="showCreateModal" class="modal-backdrop" @click.self="showCreateModal = false">
      <div class="modal-card">
        <div class="modal-head">
          <h2>Create New Ticket</h2>
          <button class="close-btn" @click="showCreateModal = false">&times;</button>
        </div>
        <form @submit.prevent="handleCreateTicket" class="modal-body">
          <div class="f-group">
            <label>Subject</label>
            <input v-model="newTicket.title" required placeholder="Short summary of the issue" />
          </div>
          <div class="f-group">
            <label>Description</label>
            <textarea v-model="newTicket.description" rows="4" required placeholder="Please provide more details..."></textarea>
          </div>
          <div class="f-row">
            <div class="f-group">
              <label>Category</label>
              <select v-model="newTicket.category">
                <option value="Hardware">Hardware</option>
                <option value="Software">Software</option>
                <option value="Network">Network</option>
                <option value="Email">Email</option>
                <option value="Printer">Printer</option>
                <option value="SAP">SAP</option>
                <option value="Access Request">Access Request</option>
                <option value="Others">Others</option>
              </select>
            </div>
            <div class="f-group">
              <label>Urgency</label>
              <select v-model="newTicket.urgency">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
          
          <div class="f-group">
            <label>Priority (SLA Basis)</label>
            <select v-model="newTicket.priority">
              <option value="LOW">Low (48h SLA)</option>
              <option value="MEDIUM">Medium (24h SLA)</option>
              <option value="HIGH">High (8h SLA)</option>
              <option value="CRITICAL">Critical (4h SLA)</option>
            </select>
          </div>
          
          <div class="f-group">
            <label>Attachment (Optional)</label>
            <div class="file-upload-wrapper">
              <label class="file-label">
                <input type="file" @change="handleFileSelect" style="display: none;" />
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                Select File
              </label>
              <span v-if="selectedFile" class="file-name">{{ selectedFile.name }} <button type="button" @click="selectedFile = null" class="clear-file">×</button></span>
            </div>
          </div>

          <div class="modal-foot">
            <button type="submit" class="btn-primary" :disabled="creating">
              {{ creating ? 'Submitting...' : 'Submit Ticket' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useRouter } from 'vue-router'
import axios from 'axios'

const authStore = useAuthStore()
const router = useRouter()
const tickets = ref<any[]>([])
const loading = ref(true)
const showCreateModal = ref(false)
const creating = ref(false)
const selectedFile = ref<File | null>(null)
const searchQuery = ref('')
const filterStatus = ref('')
const filterCategory = ref('')

const newTicket = reactive({ title: '', description: '', priority: 'MEDIUM', category: 'Others', urgency: 'Medium' })

const isAdminOrAgent = computed(() => {
  const role = (authStore.user as any)?.role
  return role === 'ADMIN' || role === 'AGENT'
})

const filteredTickets = computed(() => {
  let list = tickets.value
  if (!isAdminOrAgent.value) {
    const myId = (authStore.user as any)?.id
    list = list.filter(t => t.creator_id === myId)
  }
  
  if (searchQuery.value) {
    const s = searchQuery.value.toLowerCase()
    list = list.filter(t => t.title.toLowerCase().includes(s) || t.description?.toLowerCase().includes(s))
  }
  
  if (filterStatus.value) {
    list = list.filter(t => t.status === filterStatus.value)
  }
  
  if (filterCategory.value) {
    list = list.filter(t => t.category === filterCategory.value)
  }

  return list
})

const stats = computed(() => ({
  total: tickets.value.length,
  new: tickets.value.filter(t => t.status === 'NEW').length,
  inProgress: tickets.value.filter(t => t.status === 'IN_PROGRESS' || t.status === 'ASSIGNED').length,
  pendingUser: tickets.value.filter(t => t.status === 'PENDING_USER').length,
  resolved: tickets.value.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED').length
}))

const priorityStats = computed(() => {
  const source = isAdminOrAgent.value ? tickets.value : filteredTickets.value;
  return {
    critical: source.filter(t => t.priority === 'CRITICAL').length,
    high: source.filter(t => t.priority === 'HIGH').length,
    medium: source.filter(t => t.priority === 'MEDIUM').length,
    low: source.filter(t => t.priority === 'LOW').length,
  }
})

const last7Days = computed(() => {
  const days = []
  const today = new Date()
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    const dateStr = d.toISOString().split('T')[0]
    
    const dayTickets = tickets.value.filter(t => t.created_at.startsWith(dateStr))
    const total = dayTickets.length
    const resolved = dayTickets.filter(t => t.status === 'RESOLVED').length
    
    days.push({
      dateStr,
      label: d.toLocaleString('en-US', { month: 'short', day: 'numeric' }),
      total,
      resolved,
      openHeight: total > 0 ? 100 : 10,
      resolvedHeight: total > 0 ? (resolved / total) * 100 : 0
    })
  }
  return days
})

const userName = computed(() => (authStore.user as any)?.full_name?.split(' ')[0] || 'User')

const greeting = computed(() => {
  const h = new Date().getHours()
  if (h < 12) return 'Good Morning'
  if (h < 17) return 'Good Afternoon'
  return 'Good Evening'
})

const API_URL = (import.meta.env.VITE_API_URL || 'https://it-ticketing-api.maulanarofiq72.workers.dev').replace(/\/$/, '')

const fetchTickets = async () => {
  loading.value = true
  try {
    const response = await axios.get(`${API_URL}/tickets`)
    tickets.value = response.data
  } catch (err) { console.error('Fetch Error', err) } 
  finally { loading.value = false }
}

const handleFileSelect = (event: any) => {
  const file = event.target.files[0]
  if (file) {
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }
    selectedFile.value = file
  }
}

const handleCreateTicket = async () => {
  creating.value = true
  const slaMap = { CRITICAL: 4, HIGH: 8, MEDIUM: 24, LOW: 48 }
  const slaHours = slaMap[newTicket.priority as keyof typeof slaMap]
  const dueDate = new Date(Date.now() + slaHours * 60 * 60 * 1000).toISOString()

  try {
    const payloadData = { 
      ...newTicket, 
      due_date: dueDate,
      status: 'NEW' 
    }

    if (selectedFile.value) {
      const formData = new FormData()
      Object.entries(payloadData).forEach(([key, value]) => formData.append(key, value as string))
      formData.append('file', selectedFile.value)
      await axios.post(`${API_URL}/tickets`, formData)
    } else {
      await axios.post(`${API_URL}/tickets`, payloadData)
    }
    
    showCreateModal.value = false
    Object.assign(newTicket, { title: '', description: '', priority: 'MEDIUM', category: 'Others', urgency: 'Medium' })
    selectedFile.value = null
    await fetchTickets()
  } catch (err: any) { 
    console.error(err)
    alert(`Failed to create ticket: ${err.response?.data?.error || err.message}`) 
  }
  finally { creating.value = false }
}

const isOverdue = (date: string) => date ? new Date(date) < new Date() : false

onMounted(fetchTickets)
</script>

<style scoped>
.dashboard-page { padding: 2rem; max-width: 1400px; margin: 0 auto; background: #fbfbfc; min-height: 100vh; }

.dashboard-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 2rem; }
.greeting-section h1 { font-size: 2.25rem; font-weight: 800; color: #0f172a; margin: 0 0 0.25rem 0; letter-spacing: -0.025em; }
.subtitle { color: #64748b; font-size: 1.1rem; margin: 0; }
.header-breadcrumb { font-weight: 600; font-size: 1.1rem; margin-top: 0.5rem; }
.header-breadcrumb .muted { color: #94a3b8; }
.header-breadcrumb .active { color: #1e293b; }

.primary-btn { 
  background: #0f172a; color: white; border: none; padding: 0.6rem 1.25rem; 
  border-radius: 20px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 0.5rem;
}

.glass-card { background: white; border-radius: 16px; border: 1px solid #f1f5f9; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03); }

/* ========================================= */
/* ADMIN VIEW STYLES (HostoGo)               */
/* ========================================= */
.stats-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1rem; margin-bottom: 1.5rem; }
.stat-card { padding: 1.25rem; display: flex; flex-direction: column; gap: 1rem; }
.stat-header { display: flex; justify-content: space-between; align-items: center; }
.stat-title { color: #475569; font-weight: 600; font-size: 0.85rem; white-space: nowrap; }
.stat-value { font-size: 1.8rem; font-weight: 800; color: #0f172a; }

.middle-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem; height: 320px; }
.chart-card { padding: 1.5rem; display: flex; flex-direction: column; }
.chart-card h3 { margin: 0; font-size: 1.1rem; color: #1e293b; font-weight: 700; }
.chart-header { margin-bottom: 1rem; }
.chart-val { font-size: 2rem; font-weight: 800; color: #0f172a; display: block; margin-top: 0.5rem; }

/* CSS Area Chart */
.css-area-chart { flex: 1; position: relative; margin-top: 2rem; }
.line-path { 
  position: absolute; bottom: 0; left: 0; width: 100%; height: 100%;
  border-bottom: 3px solid #22c55e;
  clip-path: polygon(0 90%, 10% 90%, 12% 50%, 25% 50%, 35% 80%, 40% 80%, 50% 45%, 60% 50%, 75% 85%, 85% 50%, 95% 50%, 100% 70%, 100% 100%, 0 100%);
  background: transparent;
  z-index: 2;
}
.gradient-fill {
  position: absolute; bottom: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0) 100%);
  clip-path: polygon(0 90%, 10% 90%, 12% 50%, 25% 50%, 35% 80%, 40% 80%, 50% 45%, 60% 50%, 75% 85%, 85% 50%, 95% 50%, 100% 70%, 100% 100%, 0 100%);
  z-index: 1;
}
.data-point { position: absolute; left: 49.5%; top: 43%; width: 12px; height: 12px; background: white; border: 3px solid #22c55e; border-radius: 50%; z-index: 3; }
.tooltip { position: absolute; top: -50px; left: -60px; background: #0f172a; color: white; padding: 0.5rem 0.75rem; border-radius: 8px; width: 130px; display: flex; flex-direction: column; gap: 0.2rem; }
.tooltip::after { content: ''; position: absolute; bottom: -4px; left: 50%; transform: translateX(-50%); border-width: 5px 5px 0; border-style: solid; border-color: #0f172a transparent transparent transparent; }
.tooltip .t-date { font-size: 0.6rem; color: #cbd5e1; }
.tooltip .t-val { font-size: 0.85rem; font-weight: 700; }

/* CSS Gauge */
.gauge-container { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; margin-top: 1rem; }
.gauge-wrapper { width: 220px; height: 110px; position: relative; overflow: hidden; }
.gauge-arc { position: absolute; top: 0; left: 0; width: 220px; height: 220px; border-radius: 50%; border: 16px solid transparent; transform-origin: center; transition: opacity 0.3s; }
.gauge-arc.email { border-top-color: #3b82f6; border-left-color: #3b82f6; transform: rotate(-45deg); }
.gauge-arc.messenger { border-top-color: #f59e0b; border-right-color: transparent !important; transform: rotate(-90deg); }
.gauge-arc.live-chat { border-top-color: #8b5cf6; border-right-color: transparent !important; transform: rotate(0deg); }
.gauge-arc.contact-form { border-top-color: #10b981; border-right-color: transparent !important; transform: rotate(45deg); }
.gauge-center { position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); display: flex; flex-direction: column; align-items: center; background: white; width: 150px; height: 75px; border-radius: 150px 150px 0 0; padding-top: 1.5rem; }
.g-lbl { font-size: 0.75rem; color: #64748b; font-weight: 600; }
.g-val { font-size: 1.8rem; font-weight: 800; color: #0f172a; }

.legend-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 1rem; padding: 0 1rem; }
.leg-item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.8rem; color: #475569; font-weight: 600; }
.dot { width: 10px; height: 10px; border-radius: 2px; }
.dot.d-email { background: #3b82f6; } .dot.d-chat { background: #8b5cf6; } .dot.d-mess { background: #f59e0b; } .dot.d-form { background: #10b981; }

/* BOTTOM GRID */
.bottom-grid { display: grid; grid-template-columns: 1.2fr 1.5fr; gap: 1.5rem; min-height: 400px; }
.chart-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.view-btn { background: white; border: 1px solid #e2e8f0; padding: 0.4rem 0.8rem; border-radius: 8px; font-weight: 600; color: #0f172a; cursor: pointer; font-size: 0.8rem; display: inline-block; }

.bar-legends { display: flex; gap: 1.5rem; margin-bottom: 2rem; }
.square { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
.s-sol { background: #22c55e; } .s-cre { background: #bbf7d0; }
.ml { margin-left: 0.5rem; font-size: 1.2rem; color: #0f172a; }

.css-bar-chart { display: flex; height: 250px; gap: 1rem; padding-top: 1rem; border-bottom: 1px solid #f1f5f9; }
.y-axis { display: flex; flex-direction: column; justify-content: space-between; font-size: 0.75rem; color: #94a3b8; font-weight: 600; padding-bottom: 25px; }
.bars { flex: 1; display: flex; justify-content: space-around; align-items: flex-end; padding-bottom: 25px; position: relative; }
.bars::after { content: ''; position: absolute; bottom: 25px; left: 0; width: 100%; height: 1px; background: #f1f5f9; z-index: 0; }
.bar-col { width: 40px; display: flex; flex-direction: column; align-items: center; z-index: 1; position: relative; }
.b-top { width: 100%; background: #bbf7d0; border-radius: 4px 4px 0 0; }
.b-bot { width: 100%; background: #22c55e; border-radius: 0 0 4px 4px; }
.x-lbl { position: absolute; bottom: -25px; font-size: 0.75rem; color: #64748b; font-weight: 600; width: max-content; }
.h-10 { height: 10px; } .h-20 { height: 20px; } .h-30 { height: 30px; } .h-35 { height: 35px; } .h-40 { height: 40px; } .h-45 { height: 45px; } .h-50 { height: 50px; } .h-60 { height: 60px; }

.ticket-list { display: flex; flex-direction: column; gap: 0.5rem; }
.ticket-row { display: flex; align-items: center; padding: 1rem 1.5rem; background: white; border-radius: 12px; cursor: pointer; transition: box-shadow 0.2s; position: relative; overflow: hidden; border: 1px solid transparent; }
.ticket-row:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.05); border-color: #f1f5f9; }

.t-line { position: absolute; left: 0; top: 15%; height: 70%; width: 4px; border-radius: 0 4px 4px 0; }
.t-line.new { background: #64748b; } .t-line.assigned { background: #3b82f6; } .t-line.pending_user { background: #f59e0b; }
.t-line.open { background: #3b82f6; } .t-line.in_progress { background: #f59e0b; } .t-line.resolved { background: #22c55e; } .t-line.closed { background: #94a3b8; }

.t-date { width: 110px; padding-left: 0.5rem; font-weight: 600; color: #1e293b; font-size: 0.9rem; }
.t-info { flex: 1; display: flex; flex-direction: column; }
.t-title { font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; font-size: 0.95rem; }
.t-desc { font-size: 0.85rem; color: #64748b; }

.t-status { display: flex; align-items: center; gap: 1rem; width: 140px; justify-content: flex-end; }
.status-badge { padding: 0.4rem 1.25rem; border-radius: 20px; font-size: 0.75rem; font-weight: 700; text-transform: capitalize; border: 1px solid transparent; }
.status-badge.new { background: #f1f5f9; color: #475569; }
.status-badge.assigned { background: #e0f2fe; color: #0369a1; }
.status-badge.pending_user { background: #fef3c7; color: #b45309; }
.status-badge.open { background: #eff6ff; color: #2563eb; }
.status-badge.in_progress { background: #fffbeb; color: #d97706; }
.status-badge.resolved { background: #f0fdf4; color: #16a34a; }
.status-badge.closed { background: #f1f5f9; color: #475569; }

.arrow-ico { width: 24px; height: 24px; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: #0f172a; font-size: 0.8rem; }

.empty-state { text-align: center; padding: 3rem; color: #64748b; }

/* ========================================= */
/* USER VIEW STYLES                          */
/* ========================================= */
.user-stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 2.5rem; }
.user-stats-row .stat-card {
  background: white; padding: 1.25rem; border-radius: 20px; border: 1px solid #e2e8f0;
  display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  flex-direction: row; justify-content: flex-start;
}
.stat-icon { font-size: 1.5rem; background: #f8fafc; width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.stat-details .val { display: block; font-size: 1.5rem; font-weight: 800; color: #0f172a; line-height: 1; }
.stat-details .lbl { font-size: 0.75rem; font-weight: 600; color: #64748b; margin-top: 0.25rem; white-space: nowrap; }
.accent-blue { border-left: 5px solid #3b82f6; } .accent-amber { border-left: 5px solid #f59e0b; } .accent-orange { border-left: 5px solid #f97316; } .accent-green { border-left: 5px solid #10b981; }

.tickets-section { margin-bottom: 2rem; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem; }
.section-header h3 { font-size: 1.25rem; font-weight: 700; color: #1e293b; }
.filters-bar { display: flex; gap: 0.75rem; flex: 1; margin: 0 2rem; }
.search-input { flex: 1; padding: 0.5rem 1rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.875rem; }
.filter-select { padding: 0.5rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.875rem; background: white; color: #475569; }

.count-tag { background: #e2e8f0; color: #475569; padding: 0.2rem 0.75rem; border-radius: 20px; font-size: 0.75rem; font-weight: 700; }

.ticket-table-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
.user-table { width: 100%; border-collapse: collapse; text-align: left; }
.user-table th { background: #f8fafc; padding: 1rem 1.5rem; font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
.user-table td { padding: 1.25rem 1.5rem; border-top: 1px solid #f1f5f9; cursor: pointer; }
.user-table tr:hover td { background: #f8fafc; }

.id-badge { color: #2563eb; font-weight: 800; font-family: monospace; font-size: 0.9rem; }
.title-wrap { display: flex; flex-direction: column; gap: 0.4rem; }
.title-text { font-weight: 600; color: #1e293b; }
.p-tag { font-size: 0.65rem; font-weight: 800; padding: 0.1rem 0.4rem; border-radius: 4px; width: fit-content; text-transform: uppercase; }
.p-tag.critical { background: #fee2e2; color: #991b1b; } .p-tag.high { background: #ffedd5; color: #9a3412; } .p-tag.medium { background: #dcfce7; color: #166534; } .p-tag.low { background: #dbeafe; color: #1e40af; }
.s-pill { font-size: 0.75rem; font-weight: 700; padding: 0.25rem 0.75rem; border-radius: 20px; text-transform: uppercase; }
.s-pill.new { background: #f1f5f9; color: #475569; }
.s-pill.assigned { background: #e0f2fe; color: #0369a1; }
.s-pill.pending_user { background: #fef3c7; color: #b45309; }
.s-pill.open { background: #eff6ff; color: #1d4ed8; } .s-pill.in_progress { background: #fffbeb; color: #b45309; } .s-pill.resolved { background: #f0fdf4; color: #15803d; } .s-pill.closed { background: #f1f5f9; color: #475569; }

.sla-cell { font-size: 0.85rem; color: #64748b; font-weight: 500; }
.sla-cell.urgent { color: #ef4444; font-weight: 700; }
.user-empty-state { text-align: center; padding: 5rem 2rem; color: #64748b; }
.empty-art { font-size: 3rem; margin-bottom: 1rem; }

/* MODAL */
.modal-backdrop { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: white; width: 100%; max-width: 500px; border-radius: 20px; padding: 2rem; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
.modal-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
.modal-head h2 { margin: 0; font-size: 1.5rem; color: #0f172a; }
.close-btn { background: none; border: none; font-size: 1.5rem; color: #94a3b8; cursor: pointer; }
.f-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.f-group { margin-bottom: 1.25rem; display: flex; flex-direction: column; gap: 0.5rem; }
.f-group label { font-weight: 600; font-size: 0.9rem; color: #1e293b; }
.f-group input, .f-group textarea, .f-group select { padding: 0.75rem; border: 1px solid #e2e8f0; border-radius: 10px; font-family: inherit; }
.f-group input:focus, .f-group textarea:focus, .f-group select:focus { outline: none; border-color: #0f172a; }

.file-upload-wrapper { display: flex; align-items: center; gap: 1rem; margin-top: 0.5rem; }
.file-label { display: flex; align-items: center; gap: 0.5rem; color: #64748b; cursor: pointer; font-weight: 600; font-size: 0.9rem; transition: color 0.2s; }
.file-label:hover { color: #0f172a; }
.file-name { font-size: 0.8rem; background: #f1f5f9; padding: 0.3rem 0.6rem; border-radius: 6px; display: flex; align-items: center; gap: 0.5rem; color: #1e293b; }
.clear-file { background: none; border: none; color: #ef4444; cursor: pointer; font-weight: bold; font-size: 1rem; padding: 0; line-height: 1; }

.modal-foot { display: flex; justify-content: flex-end; margin-top: 2rem; }
.btn-primary { background: #0f172a; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 10px; font-weight: 600; cursor: pointer; }
</style>

